import { useCallback, useEffect, useState } from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { ArrowRightLeft, Trash2 } from "lucide-react";
import DeleteModal from "../../components/models/deleteModal.jsx";
import {
  fetchExpertRequests,
  deleteExpertRequest,
  convertExpertRequestToWarm,
} from "../../controllers/websiteController";
import DataTable, { createActionsColumn } from "../../components/data-table";

const text = (value) => value || "-";

function ExpertRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // server-side pagination + search
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [appliedSearch, setAppliedSearch] = useState("");

  // delete modal
  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  // convert-to-warm modal
  const [showConvert, setShowConvert] = useState(false);
  const [toConvert, setToConvert] = useState(null);
  const [converting, setConverting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchExpertRequests(page, limit, appliedSearch);
      setRows(res?.data || []);
      setTotal(res?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to fetch expert requests:", err);
      setError("Failed to load expert requests.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, appliedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirmDelete = async () => {
    if (!toDelete?.id) return;
    try {
      await deleteExpertRequest(toDelete.id);
      setShowDelete(false);
      setToDelete(null);
      // reload from server to keep pagination accurate
      await load();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleConfirmConvert = async () => {
    if (!toConvert?.id) return;
    try {
      setConverting(true);
      await convertExpertRequestToWarm(toConvert.id);
      setShowConvert(false);
      setToConvert(null);
      await load();
    } catch (err) {
      console.error("Convert failed:", err);
    } finally {
      setConverting(false);
    }
  };

  const columns = [
    { key: "fullName", label: "Full Name", render: text },
    { key: "email", label: "Email", render: text },
    { key: "phone", label: "Phone", render: text },
    { key: "companyName", label: "Company Name", render: text },
    { key: "companySize", label: "Company Size", render: text },
    { key: "country", label: "Country", render: text },
    { key: "employmentStatus", label: "Employment", render: text },
    { key: "industry", label: "Industry", render: text },
    { key: "jobTitle", label: "Job Title", render: text },
    {
      key: "linkedin",
      label: "LinkedIn",
      render: (url) =>
        url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[#2470B7] underline"
          >
            View <FaExternalLinkAlt aria-hidden="true" />
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
    {
      key: "convertedToWarm",
      label: "Converted",
      render: (converted) =>
        converted ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
            Converted
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
            Not Converted
          </span>
        ),
    },
    createActionsColumn(
      (r) => [
        {
          label: "Convert to warm record",
          icon: ArrowRightLeft,
          hidden: Boolean(r.convertedToWarm),
          onSelect: () => {
            setToConvert(r);
            setShowConvert(true);
          },
        },
        {
          label: "Delete",
          icon: Trash2,
          tone: "danger",
          onSelect: () => {
            setToDelete(r);
            setShowDelete(true);
          },
        },
      ],
      { getRowLabel: (r) => r.fullName || `request ${r.id}` },
    ),
  ];

  return (
    <div className="p-5 w-full max-w-full">
      <DataTable
        ariaLabel="Expert requests"
        columns={columns}
        data={rows}
        loading={loading}
        loadingRowCount={8}
        error={error}
        searchable
        manualFiltering
        searchPlaceholder="Search name / email / country / industry…"
        onSearchChange={(value) => setAppliedSearch(value.trim())}
        pagination
        page={page}
        pageSize={limit}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        labels={{ empty: "No records found" }}
      />

      {/* Delete Modal */}
      <DeleteModal
        show={showDelete}
        handleClose={() => {
          setShowDelete(false);
          setToDelete(null);
        }}
        handleConfirm={handleConfirmDelete}
        message={
          toDelete
            ? `Are you sure you want to delete "${toDelete.fullName}" (ID: ${toDelete.id})?`
            : "Are you sure you want to delete this item?"
        }
      />

      {/* Convert to Warm Record Modal */}
      <DeleteModal
        show={showConvert}
        handleClose={() => {
          setShowConvert(false);
          setToConvert(null);
        }}
        handleConfirm={handleConfirmConvert}
        loading={converting}
        title="Convert to Warm Record"
        confirmText="Convert"
        confirmingText="Converting..."
        confirmButtonClassName="bg-[#2470B7] hover:bg-[#1d5a93]"
        message={
          toConvert
            ? `Convert "${toConvert.fullName}" into a warm database record?`
            : "Convert this expert request into a warm database record?"
        }
      />
    </div>
  );
}

export default ExpertRequests;
